import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import ProductImage from '../../../components/productImage';
import { formatCurrency } from '../../../utils/format.js';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import ModalSelectProduct from './ModalSelectProduct.js';
import ProductUnitField from './ProductUnitField';
import './warehouse_branch_style.less';

const { confirm } = Modal;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 300;

/** Сонголтын өргөн — Warehouse.js-тэй ижил */
const SELECT_WIDTH = 260;

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Тоог тооцоололд оруулахад NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Branch = ({ id, products = [], form, setWarehouse, selectedCustomer, warehouses }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [branchCode, setBranchCode] = useState('');

  const addContext = useContext(AddOrderDeliveryContext);

  const branches = useMemo(() => {
    /**
     * ЗӨВХӨН бусад блок дээр сонгогдсон салбаруудыг сонголтоос хасна.
     * Формын одоогийн утгаар шүүвэл refresh хийхэд (утга сэргээгүй, харин
     * `selectedBranches` нь sessionStorage-оос сэргэсэн үед) өөрийн сонгосон
     * салбар жагсаалтаас алга болно. Warehouse.js-тэй ижил зарчим.
     */
    const takenByOtherBlocks = (addContext.selectedBranches ?? [])
      .filter((item) => item.id !== id)
      .map((item) => item.value);

    const reference = addContext?.reference?.branches;
    return {
      types: reference?.types?.filter((type) => !takenByOtherBlocks.includes(type)),
      detail: reference?.detail,
    };
  }, [addContext.selectedBranches, addContext?.reference, id]);

  useEffect(() => {
    // Refresh хийхэд формоос сэргэсэн сонголтыг локал төлөв рүү буулгана.
    // Одоогийн гэрээнд байхгүй салбар бол цэвэрлэнэ (Warehouse.js-тэй ижил зарчим).
    const fieldName = `br-code-${id}`;
    const current = form.getFieldValue(fieldName);
    const availableTypes = branches?.types;

    if (current && availableTypes?.length && !availableTypes.includes(current)) {
      form.setFieldsValue({ [fieldName]: undefined });
      setBranchCode('');
      addContext.setFormDataStep1?.((prev) => ({ ...prev, formValues: form.getFieldsValue() }));
      return;
    }

    setBranchCode(current);
    //eslint-disable-next-line
  }, [branches?.types]);

  const closeModal = () => setIsModalVisible(false);

  const onChangeBranch = (val) => {
    setBranchCode(val);
    addContext.addSelectedBranches({ id, value: val });
  };

  const onChangeWareHouse = (val) => {
    setWarehouse(val);
    addContext.addSelectedWareHouse({ id, value: val });
  };

  const onClickSelectProduct = () => {
    if (!branchCode) {
      message.error('Бараа татах салбараа сонгоно уу!');
      return;
    }
    setIsModalVisible(true);
  };

  const removeProduct = (record) =>
    confirm({
      title: 'Барааг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okButtonProps: { danger: true },
      onOk: () => {
        form.setFieldsValue(form.getFieldsValue());
        addContext.removeProductBranch(id, record._id);
      },
    });

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.imageCover}
            alt={record?.title ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #eaecf0',
              boxShadow: IMAGE_SHADOW,
            }}
          />
          <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
            <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
              {record?.title ?? '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record?.productSku}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      key: 'price',
      title: 'Нэгжийн үнэ',
      align: 'right',
      width: 140,
      render: (_, record) => formatCurrency(num(record?.price)),
    },
    {
      key: 'availableStock',
      title: 'Үлдэгдэл',
      align: 'right',
      width: 110,
      render: (_, record) => (record?.availableStock != null ? `${record.availableStock} ш` : '-'),
    },
    {
      key: 'totalUnit',
      title: 'Тоо ширхэг',
      align: 'right',
      width: 160,
      render: (_, record, index) => (
        <ProductUnitField
          form={form}
          name={['products', `branch${index}`, 'totalUnit']}
          availableStock={record?.availableStock}
          defaultValue={record?.totalUnit}
          onChangeUnit={(val) => addContext.onChangeProductUnitBranch(id, index, val)}
        />
      ),
    },
    {
      key: 'total',
      title: 'Нийт үнэ',
      align: 'center',
      width: 150,
      render: (_, record) => (
        <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
      ),
    },
    {
      key: 'action',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Устгах">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeProduct(record)}
          />
        </Tooltip>
      ),
    },
  ];

  /** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт үнэ */
  const renderSummary = (rows = []) => {
    const totalUnit = rows.reduce((sum, row) => sum + num(row?.totalUnit), 0);
    const totalPrice = rows.reduce((sum, row) => sum + num(row?.totalUnit) * num(row?.price), 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          <Text type="secondary">Нийт</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="center">
          <Text strong>{`${totalUnit} ш`}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="center">
          <Text strong>{formatCurrency(totalPrice)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
      </Table.Summary.Row>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[16, 8]} align="bottom">
        <Col flex={`0 0 ${SELECT_WIDTH}px`} style={{ maxWidth: '100%' }}>
          <Form.Item
            label="Бараа татах салбар"
            name={`br-code-${id}`}
            rules={[{ required: true, message: 'Бараа татах салбараа сонгоно уу!' }]}
            style={{ marginBottom: 0 }}
          >
            <Select placeholder="Салбар сонгох" onChange={onChangeBranch}>
              {branches?.types?.map((type) => (
                <Select.Option value={type} key={type}>
                  {branches?.detail?.[type]?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {warehouses?.types?.length > 0 && (
          <Col flex={`0 0 ${SELECT_WIDTH}px`} style={{ maxWidth: '100%' }}>
            <Form.Item
              label="Бараа хүлээн авах агуулах"
              name={`wh-code-${id}`}
              rules={[{ required: true, message: 'Агуулах сонгоно уу!' }]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder="Агуулах сонгох" onChange={onChangeWareHouse}>
                {warehouses?.types?.map((type) => (
                  <Select.Option value={type} key={type}>
                    {warehouses?.detail?.[type]?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <Col flex="auto">
          <Button type="primary" icon={<PlusOutlined />} onClick={onClickSelectProduct}>
            Бараа нэмэх
          </Button>
        </Col>
      </Row>

      {products?.length ? (
        <Table
          columns={columns}
          dataSource={products}
          rowKey={(record, index) => record?._id ?? index}
          size="middle"
          pagination={false}
          summary={renderSummary}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 13 }}>
              {branchCode
                ? '"Бараа нэмэх" товчоор бараагаа сонгоно уу.'
                : 'Эхлээд бараа татах салбараа сонгоно уу.'}
            </Text>
          }
          style={{ margin: 0, padding: '24px 0' }}
        />
      )}

      {isModalVisible && (
        <ModalSelectProduct
          visible={isModalVisible}
          onCancel={closeModal}
          selectedProducts={products}
          branchCode={branchCode}
          id={id}
          selectedCustomer={selectedCustomer}
        />
      )}
    </Space>
  );
};

export default Branch;
